import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CampaignService } from 'app/campaign/services';
import { RaffleDrawComponent } from 'app/core/components/modals/raffle-draw/raffle-draw.component';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { TicketsAndGuestsTabsPopoverComponent } from '../tickets-and-guests-tabs-popover/tickets-and-guests-tabs-popover.component';
import { loadManageCampaignList } from 'app/campaign/features/campaign.actions';
import { select, Store } from '@ngrx/store';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { TicketingSetingsComponent } from 'app/manage-tickets-and-guests/modals/ticketing-setings/ticketing-setings.component';
import { ViewRaffleWinnersComponent } from 'app/manage-tickets-and-guests/modals/view-raffle-winners/view-raffle-winners.component';
import * as moment from 'moment';
import { selectCampaignSelected, selectUpdatedTicketingCampaign } from 'app/campaign/features/campaign.selectors';
import { TicketsAndGuestsStateService } from 'app/manage-tickets-and-guests/services/tickets-and-guests-tabs.service';

@Component({
  selector: 'app-tickets-and-guests-card',
  standalone: true,
  imports: [CommonModule,TicketsAndGuestsTabsPopoverComponent],
  templateUrl: './tickets-and-guests-card.component.html',
  styleUrl: './tickets-and-guests-card.component.scss'
})
export class TicketsAndGuestsCardComponent implements OnInit,AfterViewInit{
  destroyRef = inject(DestroyRef)
  @Input() public selectedTicketingCampaign;
  public img = '/images/org_logo_default_v2.svg'
  public typeData:any;
  public linkedEvent : any | null
  public eventType :"In-Person" | "Virtual" | null = null;
  public statusType:"Active" | "Hidden" | "Ended" | "Deactivated" | "Not started"
  public eventStartFormattedDate: any | null = null;
  public userTimeZone: string | null = null;

  public showConnectToEventButton: boolean = false;

  ACTIVE_CAMPAIGN = 0;
  EXPIRED_CAMPAIGN = 1;
  DEACTIVATED_CAMPAIGN = 2;
  NOT_STARTED = 3;
  
  private ticketingCampaignUpdated$ = this.store.pipe(
    takeUntilDestroyed(),
    select(selectUpdatedTicketingCampaign)
  )
  
  constructor(
    private router:Router,
    private campaignService: CampaignService,
    private modalService: MdbModalService,
    private store:Store,
    private sharedService : SharedService,
    private stateService: TicketsAndGuestsStateService
  )
  {}

  ngAfterViewInit(): void {
  }

  ngOnInit(): void {
    if(this.selectedTicketingCampaign.imageUrl){
      this.img = this.selectedTicketingCampaign.imageUrl;
    }

    this.typeData = this.selectedTicketingCampaign.campaignTypeData;
    
    this.setLinkedEvent();

    this.showConnectToEventButton = 
      !this.typeData.linkedEventId &&
      !this.typeData.linkedVirtualEventID &&
      !this.typeData.isRaffleTicket;

    this.setTicketingCampaignStatus();

    const startDateTimeUnix = this.typeData.startDateTimeGmtUnix ?? null;
    this.eventStartFormattedDate = this.getFormattedDate(startDateTimeUnix);
    this.userTimeZone = this.getUserTimezone();
    this.ticketingCampaignUpdated$.subscribe(updatedCampaign => {
      if(!updatedCampaign || updatedCampaign.id !== this.selectedTicketingCampaign.id){
        return;
      }

      this.selectedTicketingCampaign = updatedCampaign;
    })
  }

  private setLinkedEvent() {
    if (this.typeData.linkedEventId) {
      this.linkedEvent = this.typeData.linkedEvent;
      this.eventType = "In-Person";
    }
    else if (this.typeData.linkedVirtualEventID) {
      this.linkedEvent = this.typeData.linkedVirtualEvent;
      this.eventType = "Virtual";
    }
  }

  getUserTimezone(): string {
    const userTimeZone = moment.tz.guess();
    const formattedTimeZone = new Intl.DateTimeFormat([], { timeZone: userTimeZone, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value || '';

    return formattedTimeZone;
  }

  getFormattedDate(timestamp){
    if(!timestamp) {
      return null;
    }

    const startDateTime = moment(timestamp * 1000);

    const month = startDateTime.format("MMMM").toUpperCase();
    const day = startDateTime.format("DD");
    let time = startDateTime.format("h:mma").toUpperCase()

    if (time.endsWith("00PM") || time.endsWith("00AM")) {
        time = startDateTime.format("ha");
    }

    return {
      month,
      day,
      time
    }
  }

  setTicketingCampaignStatus(){
    if(this.selectedTicketingCampaign.status === this.ACTIVE_CAMPAIGN){
      this.statusType = this.selectedTicketingCampaign.isDefault ? "Active" : "Hidden"
      return;
    }

    if(this.selectedTicketingCampaign.status === this.EXPIRED_CAMPAIGN){
      this.statusType = "Ended"
      return;
    }

    if(this.selectedTicketingCampaign.status === this.DEACTIVATED_CAMPAIGN){
      this.statusType = "Deactivated"
      return;
    }
    
    if(this.selectedTicketingCampaign.status === this.NOT_STARTED){
      this.statusType = "Not started"
    }
  }

  openTicketingDetailsPage(event){
    if(!event.target.matches('.noDetaisClick')){
      this.stateService.selectedTab = 'tickets'
      this.stateService.selectedCampaign = this.selectedTicketingCampaign;
      this.router.navigate(['/manage/tickets-and-guests/campaign', this.selectedTicketingCampaign.id], { fragment: 'ticketsTab' });
    }
  }

  modalRef :MdbModalRef<any>;
  onDrawModalClick(){
    this.campaignService.raffleDraw(this.selectedTicketingCampaign.id)
    .pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data:any)=>{
      let isArray = false
      if(Array.isArray(data)){
        isArray = true
      }
      this.modalRef = this.modalService.open(RaffleDrawComponent, {
        ignoreBackdropClick: false,
        modalClass:`modal--paybee ${ isArray ? 'modal-lg' : ''}`,
        containerClass:'mdb-modal-backdrop-paybee',
        data: {
          isArray,
          campaign:this.selectedTicketingCampaign,
          data,
          onClose: ()=>{
            this.modalRef.close()
          }
        }
      });
    })
  }

  onShowWinnersClick(){
   let isSimpleRaffle = false;
   let campaign = this.selectedTicketingCampaign;
   const raffleWinners = this.typeData.basketRaffleWinners;
   for(let i =0;i<raffleWinners?.length; ++i){
    raffleWinners[i].Winner = raffleWinners[i].winnerUser;
    const basketRaffleItem = this.typeData.basketRaffleItems.find(item => item.id === raffleWinners[i].basketRaffleOptionId)
    raffleWinners[i].BasketRaffleItem = basketRaffleItem;
   }

   const simpleBacketWinners = this.typeData.raffleWinners;
   const data = raffleWinners ?? simpleBacketWinners;
   if(raffleWinners){
    isSimpleRaffle = false;
   }
   else if(simpleBacketWinners){
    isSimpleRaffle = true;
   }

    this.modalRef = this.modalService.open(ViewRaffleWinnersComponent, {
      ignoreBackdropClick: false,
      modalClass:`modal--paybee ${isSimpleRaffle ? '' : 'modal-lg'}`,
      containerClass:'mdb-modal-backdrop-paybee',
      data: {
        isSimpleRaffle,
        campaign,
        data,
        onClose: ()=>{
          this.modalRef.close()
        }
      }
    });
  
  }

  onConnectEvent(){
    const reloadList = () => {
      this.store.dispatch({
            type: loadManageCampaignList.type,
            payload: this.sharedService.token,
            campaignTypes:[6],
          });
    }
    window['ticketingAndGuestsModal'].connectCampaignToEvent(this.typeData, this.selectedTicketingCampaign,reloadList)
  }

  campaignSelectedSelector$ = this.store.pipe(
    select(selectCampaignSelected),
    takeUntilDestroyed(this.destroyRef),

  )
  onSettingsModalOpen(){
    this.modalRef = this.modalService.open(TicketingSetingsComponent, {
      ignoreBackdropClick: false,
      containerClass:'mdb-modal-backdrop-paybee',
      data: {
        campaign: this.selectedTicketingCampaign,
        onClose: ()=>{
          this.modalRef.close()
        }
      }
    });
  }
}
